import { Link } from 'react-router-dom';
import {
  INSURANCE_MIN_CENTS, INSURANCE_RATE, SIGNATURE_THRESHOLD_CENTS, usd,
  PICKUP_ENABLED,
  SHIP_WINDOW,
} from '../lib/shippingPolicy';

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 34 }}>
    <h2 style={{
      fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: 'var(--accent)', marginBottom: 12,
    }}>{title}</h2>
    {children}
  </section>
);

const Row = ({ label, value, note }) => (
  <div style={{
    display: 'flex', alignItems: 'baseline', gap: 16, padding: '11px 0',
    borderBottom: '1px solid var(--border)',
  }}>
    <span style={{ flex: 1, fontWeight: 700 }}>{label}</span>
    <span style={{ textAlign: 'right' }}>
      <span style={{ fontWeight: 800 }}>{value}</span>
      {note && (
        <span style={{ display: 'block', fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {note}
        </span>
      )}
    </span>
  </div>
);

export default function ShippingPage() {
  const pct = (INSURANCE_RATE * 100).toFixed(2).replace(/0$/, '');

  return (
    <div className="sv-container" style={{ paddingBlock: '54px 80px', maxWidth: 720 }}>
      <h1 style={{ fontSize: '1.7rem', fontWeight: 900, marginBottom: 8 }}>Shipping and pricing</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 34, lineHeight: 1.6 }}>
        What it costs to get your cards, and how they are protected on the way. No handling fees,
        no markup on postage — you pay what the carrier charges us.
      </p>

      <Section title="Getting your order">
        {PICKUP_ENABLED && (
          <Row label="Local pickup" value="Free"
               note="We email you when it is ready. Nothing to pay for shipping." />
        )}
        <Row label="Shipped to you" value="Carrier rate"
             note="USPS and UPS, quoted live at checkout once you enter your address." />
        <Row label="Handling fee" value="None"
             note="Boxes, sleeves, and packing are on us." />
      </Section>

      <Section title="Insurance">
        <Row label={`Orders under ${usd(INSURANCE_MIN_CENTS)}`} value="Carrier coverage"
             note={`USPS Ground Advantage and UPS both include ${usd(INSURANCE_MIN_CENTS)} of coverage in the label price, so there is nothing to add.`} />
        <Row label={`Orders over ${usd(INSURANCE_MIN_CENTS)}`} value={`${pct}% of the order`}
             note="Insured for the full declared value. The cost is included in the shipping price you see at checkout." />
        <Row label={`Orders over ${usd(SIGNATURE_THRESHOLD_CENTS)}`} value="Signature required"
             note="Someone has to be there to sign for it. Added at no extra charge to you beyond the carrier rate." />
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 14, lineHeight: 1.6 }}>
          Example: a {usd(65000)} sealed box ships insured for its full value, which adds about{' '}
          {usd(Math.ceil(65000 * INSURANCE_RATE))} to the shipping line, and goes out signature required.
        </p>
      </Section>

      <Section title="How it ships">
        <Row label="Singles" value="Sleeved, toploaded, team bag"
             note="Rigid mailer or bubble mailer depending on value." />
        <Row label="Graded slabs" value="Boxed"
             note="Snug fit, padded, never loose in the box." />
        <Row label="Sealed product" value="Boxed with fill"
             note="Shipped in a box sized to the product so it does not shift." />
      </Section>

      <Section title="Timing">
        <Row label="When it goes out" value={`${SHIP_WINDOW.replace('We ship within ', '')} after you order`}
             note="Your order is packed and handed to the carrier within that window. You get the tracking number the moment the label is bought." />
        <Row label="Delivery estimate" value="Shown at checkout"
             note="Every option lists the carrier's own business-day estimate before you choose." />
        <Row label="Tracking" value="Emailed"
             note="You get the tracking number as soon as the label is bought." />
      </Section>

      <Section title="If something goes wrong">
        <p style={{ lineHeight: 1.7 }}>
          If a package is lost or arrives damaged, contact us before doing anything else and keep all
          the packaging. Insured orders are filed on your behalf. Uninsured orders under{' '}
          {usd(INSURANCE_MIN_CENTS)} fall under the carrier's included coverage, which we will also
          file for you.
        </p>
      </Section>

      <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
        <Link to="/shop" style={{ fontWeight: 800 }}>Back to the shop</Link>
        <Link to="/order/lookup" style={{ fontWeight: 800, color: 'var(--text-muted)' }}>Track an order</Link>
      </div>
    </div>
  );
}
